import { useState, useEffect, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import "../InvoicesManagement.scss";
import InformationBox from "../../components/InformationBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import RoundedTable from "../../components/table-component/RoundedTable";
import { TABLE_CELL_TYPES } from "../../components/table-component/RoundedTable";

import AddEmployeeEntitlementModal from "./AddEmployeeEntitlementModal";
import PageWrapper from "../../components/PageWrapper";
import ErrorBox from "../../components/ErrorBox";
import CustomButton from "../../components/CustomButton";
import addIconLight from "../../assets/add_icon_light.png";
import deleteIcon from "../../assets/delete_Icon.png";
import axiosClient from "../../services/axiosClient";
import { useParams } from "react-router-dom";
import AreYouSureModal from "../../components/AreYouSureModal";

const EmployeeEntitlementsPage = () => {
  const { t } = useTranslation("general");

  const [isFetchingEntitlements, setIsFetchingEntitlements] = useState(false);
  const [fetchingEntitlementsError, setFetchingEntitlementsError] =
    useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [entitlements, setEntitlements] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { id } = useParams();

  const tableTitles = [
    { value: "entitlement", keepOnMobile: true },
    { value: "date", keepOnMobile: true },
    { value: "disbursementDate", keepOnMobile: true },
    { value: "amount", keepOnMobile: true },
    { value: "notes", keepOnMobile: true },
  ];

  const titles = [
    { title: "", value: "select" },
    { title: t("entitlement"), value: "entitlement" },
    { title: t("date"), value: "date" },
    { title: t("disbursementDate"), value: "disbursementDate" },
    { title: t("amount"), value: "amount" },
    { title: t("notes"), value: "notes" },
  ];

  // Data fetching
  const getAllEntitlementsData = async (page, searchQuery) => {
    try {
      setIsFetchingEntitlements(true);
      const response = await axiosClient.get(
        "/userEntitlements/getUserEntitlements",
        {
          params: {
            page,
            searchQuery,
            userId: id,
          },
        }
      );

      const entitlements = response.data.userEntitlements;

      setEntitlements(entitlements);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response?.data?.totalPages,
        totalRecords: response?.data?.totalUserEntitlements,
      });
    } catch (error) {
      console.error(
        "Error fetching entitlements:",
        error.response?.data || error
      );
      setFetchingEntitlementsError("errorFetchingEntitlements");
    } finally {
      setIsFetchingEntitlements(false);
    }
  };

  const tableRows = entitlements.map((employeeEntitlement, index) => [
    {
      value: employeeEntitlement.entitlement.name,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: employeeEntitlement.date,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value: employeeEntitlement.disbursementDate,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value: employeeEntitlement.entitlement.amount,
      type: TABLE_CELL_TYPES.MONEY,
      keepOnMobile: true,
      currency: "IQD",
    },
    {
      value: employeeEntitlement.notes || t("noNotes"),
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
  ]);

  // Effects
  useEffect(() => {
    getAllEntitlementsData(pagination.currentPage, searchQuery);
  }, [pagination.currentPage]);


  return (
    <PageWrapper title={t("employeeEntitlements")} withBackButton>
      <div className="u-margin--top_700" />
      <div className="o-row o-row--horizontal-align_end u-margin--bottom_400 o-row--gap-size_500">
        {selectedRows.length > 0 && (
          <CustomButton
            title={t("delete")}
            icon={deleteIcon}
            iconSize={20}
            variant={"red"}
            onClick={() => setShowDeleteModal(true)}
          />
        )}
        <CustomButton
          onClick={() => setShowAddModal(true)}
          title={t("addEntitlement")}
          icon={addIconLight}
          iconSize={20}
        />
      </div>

      {fetchingEntitlementsError && (
        <ErrorBox>{t(fetchingEntitlementsError)}</ErrorBox>
      )}
      {isFetchingEntitlements && <Loader size={LOADER_SIZES.MEDIUM} />}

      {!fetchingEntitlementsError &&
        !isFetchingEntitlements &&
        entitlements.length === 0 && (
          <>
            <InformationBox>{t("noEntitlementsFound")}</InformationBox>
          </>
        )}

      {!fetchingEntitlementsError &&
        !isFetchingEntitlements &&
        entitlements.length > 0 && (
          <>
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
              withCheckbox
              fullData={entitlements}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
            />
          </>
        )}
      {showDeleteModal && (
        <DeleteModal
          setShowDeleteModal={setShowDeleteModal}
          selectedRows={selectedRows}
          onSuccess={() => {
            setShowDeleteModal(false);
            getAllEntitlementsData(pagination.currentPage, searchQuery);
                        setSelectedRows([]);
          }}
        />
      )}
      {showAddModal && (
        <AddEmployeeEntitlementModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            getAllEntitlementsData(pagination.currentPage, searchQuery);
            setSelectedRows([]);
          }}
          employeeId={id}
        />
      )}
    </PageWrapper>
  );
};

function DeleteModal({ setShowDeleteModal, selectedRows, onSuccess }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingError, setDeletingError] = useState(null);
  const { t } = useTranslation("general");

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await axiosClient.delete(
        "/userEntitlements/deleteMultipleUsersEntitlements",
        {
          data: {
            ids: selectedRows.map((row) => row.id),
          },
        }
      );

      onSuccess();
    } catch (error) {
      console.error(
        "Error deleting entitlements:",
        error.response?.data || error
      );
      setDeletingError("errorDeletingEntitlements");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AreYouSureModal
      onClose={() => setShowDeleteModal(false)}
      onConfirm={handleDelete}
      customMessage={t("areYouSureToDeleteEntitlements")}
      closeTitle="cancel"
      confirmTitle="delete"
      loading={isDeleting}
      confirmButtonVariant="red"
      error={t(deletingError)}
    />
  );
}

export default EmployeeEntitlementsPage;
